/* DOLO'S WAY: NIGHT MAZE — constants, tuning, and the deterministic maze.
   One fixed maze layout (seeded gen, same for every player) so competition is fair. */

export const CELL = 40;
export const COLS = 15;
export const ROWS = 23;
export const W = COLS * CELL; // 600
export const H = ROWS * CELL; // 920 — tall vertical cabinet maze

export const NIGHT_FRAMES = 150 * 60; // 2.5-minute night
export const EXIT_OPENS_AT = 30 * 60; // the gate grinds open 30s before dawn

/* local mulberry32 (same algorithm as wasteland/constants) — maze layout only */
export const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/* DAILY NIGHT SEED — one shared maze per UTC day. djb2 of the date key,
   mirrored by _daily_seed in backend/server.py so the vault agrees. */
export const dailyKey = (d = new Date()) => d.toISOString().slice(0, 10);

export const seedFromKey = (key) => {
  let h = 5381;
  for (let i = 0; i < key.length; i += 1) h = ((h << 5) + h + key.charCodeAt(i)) >>> 0;
  return h;
};

/* NIGHTLY SKY — the daily seed also picks tonight's palette, same sky for every
   runner: ash static, toxic green CRT, or blood-red dusk */
export const SKINS = [
  { key: "ash", name: "ASH STATIC", floor: "#08080d", dark: "3, 4, 12", wash: null, accent: "#ff3355" },
  { key: "toxic", name: "TOXIC GREEN CRT", floor: "#040a06", dark: "2, 12, 6", wash: "rgba(70, 255, 154, 0.09)", accent: "#46ff9a" },
  { key: "blood", name: "BLOOD-RED DUSK", floor: "#0d0507", dark: "14, 3, 6", wash: "rgba(255, 51, 85, 0.09)", accent: "#ff6a3c" },
];

export const skinForDay = (d = new Date()) => SKINS[seedFromKey(dailyKey(d)) % SKINS.length];

/* RUNNER ROSTER — pickable night runners. All runners carry their owner-supplied
   key art as transparent sprites (dolo included — the animated panda frames in
   SPRITES.dolo remain only as a legacy fallback) */
export const NIGHT_RUNNERS = [
  { key: "dolo", name: "DOLO", title: "THE WASTELAND RUNNER", sprite: "/assets/night-runners/dolo.webp" },
  { key: "woofer", name: "MAGNUS", title: "RIFLE VETERAN", sprite: "/assets/night-runners/woofer.webp" },
  { key: "blackout", name: "ARIEL", title: "SWORD OF THE WASTES", sprite: "/assets/night-runners/blackout.webp" },
  { key: "socket", name: "ORICOLE", title: "PINK-CORE FRAME", sprite: "/assets/night-runners/socket.webp" },
  { key: "killswitch", name: "XO77", title: "DRONE-RIG HUNTER", sprite: "/assets/night-runners/killswitch.webp" },
  { key: "riff", name: "RIFF", title: "BOT-BARD OF THE DUNES", sprite: "/assets/night-runners/riff.webp" },
];

/* recursive-backtracker maze, braided (extra walls knocked out for loops),
   deterministic from the given seed */
export const buildMaze = (seed) => {
  const rng = mulberry32(seed);
  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill("#"));
  const carve = (cx, cy) => {
    const dirs = [[2, 0], [-2, 0], [0, 2], [0, -2]].sort(() => rng() - 0.5);
    for (const [dx, dy] of dirs) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx > 0 && nx < COLS - 1 && ny > 0 && ny < ROWS - 1 && grid[ny][nx] === "#") {
        grid[cy + dy / 2][cx + dx / 2] = ".";
        grid[ny][nx] = ".";
        carve(nx, ny);
      }
    }
  };
  grid[1][1] = ".";
  carve(1, 1);
  // braid: open ~14% of remaining interior walls so there are loops to run
  for (let y = 2; y < ROWS - 2; y += 1) {
    for (let x = 2; x < COLS - 2; x += 1) {
      if (grid[y][x] === "#" && rng() < 0.14) {
        const horizontal = grid[y][x - 1] === "." && grid[y][x + 1] === ".";
        const vertical = grid[y - 1][x] === "." && grid[y + 1][x] === ".";
        if (horizontal || vertical) grid[y][x] = ".";
      }
    }
  }
  // hide spots: doors + vents carved into quiet floor pockets
  const floor = [];
  for (let y = 1; y < ROWS - 1; y += 1)
    for (let x = 1; x < COLS - 1; x += 1) if (grid[y][x] === ".") floor.push([x, y]);
  // SANCTUARY — a lit safehouse at the maze heart. The horde cannot cross its
  // threshold; runners catch their breath here. Guaranteed open approach lanes.
  const scx = Math.floor(COLS / 2);
  const scy = Math.floor(ROWS / 2);
  grid[scy][scx] = "S";
  [[1, 0], [-1, 0], [0, 1], [0, -1]].forEach(([dx, dy]) => {
    if (grid[scy + dy][scx + dx] === "#") grid[scy + dy][scx + dx] = ".";
  });

  const hidePicks = [];
  const spawnCorners = [[1, 1], [COLS - 2, 1], [1, ROWS - 2], [COLS - 2, ROWS - 2]];
  while (hidePicks.length < 7 && floor.length) {
    const pick = floor.splice(Math.floor(rng() * floor.length), 1)[0];
    // keep hides off the sanctuary, runner spawns, the exit mouth, and each other
    if (pick[0] === scx && pick[1] === scy) continue;
    if (spawnCorners.some(([sx, sy]) => Math.abs(sx - pick[0]) + Math.abs(sy - pick[1]) <= 2)) continue;
    // keep hideout dens clear of both side-door mouths
    if (Math.abs(pick[0] - 1) + Math.abs(pick[1] - Math.floor(ROWS / 2)) <= 2) continue;
    if (Math.abs(pick[0] - (COLS - 2)) + Math.abs(pick[1] - Math.floor(ROWS / 2)) <= 2) continue;
    if (hidePicks.every(([hx, hy]) => Math.abs(hx - pick[0]) + Math.abs(hy - pick[1]) > 4)) hidePicks.push(pick);
  }
  hidePicks.forEach(([x, y], i) => {
    grid[y][x] = i % 2 ? "V" : "D";
  });
  // two side doors — left and right edges, sealed until the final 30 seconds
  const doorY = Math.floor(ROWS / 2);
  grid[doorY][0] = "E";
  grid[doorY][COLS - 1] = "E";
  // mouth corridors so both doors are always reachable
  for (const dy of [-1, 0, 1]) {
    grid[doorY + dy][1] = ".";
    grid[doorY + dy][COLS - 2] = ".";
  }
  return grid;
};

export const MAZE = buildMaze(seedFromKey(dailyKey()));
export const EXITS = [
  { x: 0, y: Math.floor(ROWS / 2), name: "LEFT DOOR" },
  { x: COLS - 1, y: Math.floor(ROWS / 2), name: "RIGHT DOOR" },
];

export const SPAWNS = [
  { x: 1, y: 1 },
  { x: COLS - 2, y: 1 },
  { x: 1, y: ROWS - 2 },
  { x: COLS - 2, y: ROWS - 2 },
];

export const RUNNER_COLORS = ["#ff3355", "#ffb020", "#46e0d4", "#9a6bff"];
export const CPU_NAMES = ["KAELEN", "RIFF", "VEX"];

export const RUNNER_SPEED = 2.5;
export const ZOMBIE_SPEED = 1.15;
export const DOG_SPEED = 1.6;
export const DOG_CHARGE_SPEED = 3.4;
export const ZOMBIE_START = 3;
export const ZOMBIE_CAP = 6;
export const ZOMBIE_ADD_EVERY = 30 * 60;
export const DOWN_FRAMES = 180;
export const INVULN_FRAMES = 150;

/* sword slash — the blade auto-turns to the nearest threat in reach; drops zombies, stuns dogs */
export const SLASH_FRAMES = 14;
export const SLASH_CD = 40;
export const SLASH_REACH = 66;
/* kill streak — eliminations within this window chain into a RAMPAGE */
export const RAMPAGE_WINDOW = 300;
export const RAMPAGE_AT = 3;

/* power items — one-slot inventory, E / Enter / H / R to fire */
export const POWER_ITEMS = {
  flare: { name: "FLARE GUN", icon: "/assets/dolosnight/drive.png", desc: "Repels every zombie and dog for 6s", duration: 360 },
  adrenaline: { name: "ADRENALINE", icon: "/assets/dolosnight/bottle.png", desc: "+60% speed for 8s", duration: 480 },
  lure: { name: "BOOMBOX LURE", icon: "/assets/dolosnight/crate-2.png", desc: "Thrown lure drags the horde away, then blows", duration: 300 },
  armor: { name: "SCRAP ARMOR", icon: "/assets/dolosnight/medkit.png", desc: "Blocks one takedown", duration: 0 },
  smoke: { name: "SMOKE CANISTER", icon: "/assets/dolosnight/energy.png", desc: "Invisible for 5s — even while moving", duration: 300 },
};

export const SPRITES = {
  dolo: {
    n: ["/assets/dolosnight/dolo-n.png", "/assets/dolosnight/dolo-n2.png", "/assets/dolosnight/dolo-n3.png"],
    s: ["/assets/dolosnight/dolo-s.png", "/assets/dolosnight/dolo-s2.png"],
    e: ["/assets/dolosnight/dolo-e.png"],
  },
  zombies: [
    "/assets/dolosnight/zombie-1.png",
    "/assets/dolosnight/zombie-2.png",
    "/assets/dolosnight/zombie-3.png",
    "/assets/dolosnight/zombie-4.png",
    "/assets/dolosnight/zombie-5.png",
  ],
  dogs: ["/assets/dolosnight/dog-1.png", "/assets/dolosnight/dog-2.png", "/assets/dolosnight/dog-3.png", "/assets/dolosnight/dog-4.png"],
  walls: ["/assets/dolosnight/wall-stone.png", "/assets/dolosnight/wall-brick.png"],
  decor: ["/assets/dolosnight/rubble.png", "/assets/dolosnight/car.png", "/assets/dolosnight/sandbags.png", "/assets/dolosnight/tires.png", "/assets/dolosnight/tree.png", "/assets/dolosnight/debris.png"],
  lamp: "/assets/dolosnight/lamp.png",
  door: "/assets/dolosnight/door.png",
  vent: "/assets/dolosnight/vent.png",
  supply: ["/assets/dolosnight/can.png", "/assets/dolosnight/water.png"],
  medkit: "/assets/dolosnight/medkit.png",
  crate: "/assets/dolosnight/crate.png",
  lore: ["/assets/dolosnight/diary.png", "/assets/dolosnight/drive.png"],
};

export const isWall = (tx, ty) => {
  if (tx < 0 || ty < 0 || tx >= COLS || ty >= ROWS) return true;
  const c = MAZE[ty][tx];
  return c === "#";
};

export const tileAt = (tx, ty) => (tx < 0 || ty < 0 || tx >= COLS || ty >= ROWS ? "#" : MAZE[ty][tx]);

export const tileCenter = (tx, ty) => ({ x: tx * CELL + CELL / 2, y: ty * CELL + CELL / 2 });
